import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { notificationsApi } from '../../services/api';
import { secureStorage } from '../../utils/secureStorage';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Bell,
  Clock,
  Calendar,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  X,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch stored notifications on mount
  useEffect(() => {
    const fetchStoredNotifications = async () => {
      if (!user || !['admin', 'accounting_admin', 'manager'].includes(user.role)) return;
      
      try {
        const stored = await notificationsApi.getAll({ limit: 20 });
        if (stored && stored.length > 0) {
          setNotifications(stored);
          setUnreadCount(stored.filter(n => !n.read).length);
        }
      } catch (e) {
        console.error('Failed to fetch notifications:', e);
      }
    };
    
    fetchStoredNotifications();
  }, [user]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user) return;
    
    const token = secureStorage.getItem('token');
    if (!token) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Create WebSocket URL
    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/notifications/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            return;
          }
          
          if (data.type === 'pong') return;

          // Add new notification
          setNotifications(prev => [data, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + 1);

          // Play sound for urgent notifications
          if (data.type === 'late_clock_in' || data.type === 'urgent_message') {
            playNotificationSound();
          }
        } catch (e) {
          console.error('Failed to parse notification:', e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      // Keep-alive ping every 30 seconds
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
      };
    } catch (e) {
      console.error('Failed to connect WebSocket:', e);
    }
  }, [user]);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'accounting_admin' || user.role === 'manager')) {
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user, connectWebSocket]);

  const playNotificationSound = () => {
    // Create a simple beep sound
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
    } catch (e) {
      console.warn('Notification sound not supported:', e.message);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'clock_in':
        return <Clock className="w-4 h-4 text-purple-500" />;
      case 'clock_out':
        return <Clock className="w-4 h-4 text-violet-500" />;
      case 'late_clock_in':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'time_off_request':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'time_off_approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'time_off_denied':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'urgent_message':
        return <MessageSquare className="w-4 h-4 text-red-500" />;
      case 'new_message':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'late_clock_in':
      case 'time_off_denied':
      case 'urgent_message':
        return 'bg-red-50 border-l-4 border-l-red-500';
      case 'time_off_approved':
        return 'bg-green-50 border-l-4 border-l-green-500';
      case 'time_off_request':
        return 'bg-blue-50 border-l-4 border-l-blue-500';
      default:
        return 'bg-purple-50 border-l-4 border-l-purple-500';
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Persist to backend
    try {
      await notificationsApi.markAsRead(notificationId);
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    // Persist to backend
    try {
      await notificationsApi.markAllAsRead();
    } catch (e) {
      console.error('Failed to mark all notifications as read:', e);
    }
  };

  const clearAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);
    
    // Clear from backend
    try {
      await notificationsApi.clearAll();
    } catch (e) {
      console.error('Failed to clear notifications:', e);
    }
  };

  const dismissNotification = (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Only show for managers and admins
  if (!user || !['admin', 'accounting_admin', 'manager'].includes(user.role)) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative p-2" data-testid="notification-bell">
          <Bell className={`w-5 h-5 ${isConnected ? 'text-gray-600' : 'text-gray-400'}`} />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {!isConnected && (
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-amber-400 rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {!isConnected && (
              <span className="text-xs text-amber-600">(reconnecting...)</span>
            )}
          </div>
          {notifications.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllNotifications} className="text-xs text-gray-500">
              Clear all
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {notifications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer relative ${
                    !notification.read ? getNotificationColor(notification.type) : ''
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                    onClick={(e) => { e.stopPropagation(); dismissNotification(notification.id); }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                  <div className="flex gap-3 pr-6">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {notification.timestamp && formatDistanceToNow(parseISO(notification.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-12 text-gray-500">
              <Bell className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">
                You&apos;ll see alerts here when employees clock in or request time off
              </p>
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
